import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { deleteUser, getUser } from "../../auth/Auth";
import { postRequest } from "../../routes/Routes";
import { motion } from "framer-motion";
import "../../assets/css/Sidebar.css";
import { Cake, QrCode, Plus, LogOut, Home, Menu, X, Settings, Palette, Users, MessageSquareQuote, Utensils } from "lucide-react";

const FULL_W = 256; // 16rem
const RAIL_W = 64;  // 4rem

const Sidebar = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(undefined);

    // responsive: collapse on tablets and below
    const [compact, setCompact] = useState(false);
    const [expandedOnCompact, setExpandedOnCompact] = useState(false);

    useEffect(() => {
        getUser()
            .then((u) => setUser(u ?? null))
            .catch(() => setUser(null));
    }, []);

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 991.98px)");
        const onChange = () => setCompact(mq.matches);
        onChange();
        if (mq.addEventListener) mq.addEventListener("change", onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    // auto collapse after route change on compact
    useEffect(() => {
        if (compact) setExpandedOnCompact(false);
    }, [location.pathname, compact]);

    const handleLogout = async () => {
        if (loading) return;
        setLoading(true);
        try {
            const token = user?.token;
            if (token) await postRequest({ endpoint: "/api/logout", token });
        } catch { }
        await deleteUser();
        window.location.replace("/login");
    };

    // derive role (1 = Admin, 2 = Basic) similar to portal pages
    const roleId = Number(user?.role_id ?? user?.role?.id ?? user?.roleId);
    const isAdmin = roleId === 1;

    const baseMenu = [
        { to: "/portal/qrs", icon: QrCode, label: "All QR Codes", end: true },
        { to: "/portal/qrs/new", icon: Plus, label: "Create New QR" },
        { to: "/portal/template-manager", icon: Palette, label: "Manage Templates" },
        { to: "/portal/users", icon: Users, label: "Manage Users", adminOnly: true },
        { to: "/portal/testimonials", icon: MessageSquareQuote, label: "Manage Testimonials", adminOnly: true },
        { to: "/portal/menus", icon: Utensils, label: "Manage Menus", adminOnly: true },
    ];

    const menu = baseMenu.filter((item) => !item.adminOnly || isAdmin);

    const expanded = !compact || expandedOnCompact;
    const widthPx = expanded ? FULL_W : RAIL_W;

    return (
        <motion.aside
            initial={{ x: -250 }}
            animate={{ x: 0 }}
            transition={{ duration: 0.3 }}
            className={`sb ${expanded ? "sb--expanded" : "sb--collapsed"} bg-white shadow-lg border-end d-flex flex-column h-100 position-relative`}
            style={{ width: widthPx, flex: `0 0 ${widthPx}px` }}
        >
            {/* Header */}
            <div className="sb-header border-bottom p-3 position-relative">
                <div className={expanded ? "d-flex align-items-center gap-3" : "d-flex flex-column align-items-center"}>
                    {/* Logo always visible */}
                    <div
                        className="sb-logo gradient-secondary rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: 40, height: 40 }}
                    >
                        <Cake className="text-white" size={24} />
                    </div>

                    {/* Title only when expanded */}
                    {expanded && (
                        <div className="text-truncate">
                            <h1 className="fs-6 fw-bold text-dark font-secondary mb-0">Keek Portal</h1>
                            <p className="small text-muted font-primary mb-0">QR Management</p>
                        </div>
                    )}

                    {/* Compact & collapsed: show Menu button BELOW logo */}
                    {compact && !expanded && (
                        <button
                            type="button"
                            className="sb-toggle-under-logo btn btn-light mt-2 d-inline-flex align-items-center justify-content-center"
                            aria-label="Expand sidebar"
                            title="Expand"
                            onClick={() => setExpandedOnCompact(true)}
                        >
                            <Menu size={18} />
                        </button>
                    )}
                </div>

                {/* Compact & expanded: show Close button ON THE RIGHT EDGE */}
                {compact && expanded && (
                    <button
                        type="button"
                        className="sb-close btn btn-light d-inline-flex align-items-center justify-content-center"
                        aria-label="Collapse sidebar"
                        title="Close"
                        onClick={() => setExpandedOnCompact(false)}
                    >
                        <X size={18} />
                    </button>
                )}
            </div>

            {/* Navigation */}
            <nav className="sb-nav flex-fill p-2">
                <ul className="list-unstyled mb-0">
                    {menu.map((item) => {
                        const Icon = item.icon;
                        return (
                            <li key={item.to} className="mb-2">
                                <NavLink
                                    to={item.to}
                                    end={item.end}
                                    title={expanded ? undefined : item.label}
                                    className={({ isActive }) =>
                                        [
                                            "d-flex align-items-center",
                                            expanded ? "gap-3 px-3 py-3 rounded-3" : "justify-content-center py-2 mx-1 rounded-3",
                                            isActive ? "gradient-secondary text-white shadow fw-medium" : "text-dark",
                                            "text-decoration-none font-secondary",
                                        ].join(" ")
                                    }
                                >
                                    <Icon size={20} />
                                    {expanded ? (
                                        <span className="text-truncate">{item.label}</span>
                                    ) : (
                                        <span className="visually-hidden">{item.label}</span>
                                    )}
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {/* Footer — unchanged full buttons when expanded; icon-only when collapsed */}
            <div className="p-3 border-top">
                {expanded ? (
                    <>
                        <button
                            onClick={() => navigate("/")}
                            className="btn btn-outline-secondary w-100 d-flex align-items-center gap-3 py-3 rounded-3 fw-medium mb-2"
                        >
                            <Home size={20} />
                            Back to Website
                        </button>

                        <button
                            onClick={() => navigate("/portal/user-settings")}
                            className="btn btn-outline-primary w-100 d-flex align-items-center gap-3 py-3 rounded-3 fw-medium mb-2"
                        >
                            <Settings size={20} />
                            User Settings
                        </button>

                        <button
                            onClick={handleLogout}
                            disabled={loading}
                            className="btn btn-outline-danger w-100 d-flex align-items-center gap-3 py-3 rounded-3 fw-medium"
                        >
                            {loading ? (
                                <span className="spinner-grow spinner-grow-sm me-2" role="status" aria-hidden="true" />
                            ) : null}
                            <LogOut size={20} />
                            Sign Out
                        </button>
                    </>
                ) : (
                    <div className="d-flex flex-column align-items-center gap-2">
                        <button
                            onClick={() => navigate("/")}
                            className="btn btn-outline-secondary rounded-circle d-flex align-items-center justify-content-center"
                            title="Back to Website"
                            style={{ width: 40, height: 40 }}
                        >
                            <Home size={18} />
                        </button>
                        <button
                            onClick={() => navigate("/portal/user-settings")}
                            className="btn btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                            title="User Settings"
                            style={{ width: 40, height: 40 }}
                        >
                            <Settings size={18} />
                        </button>
                        <button
                            onClick={handleLogout}
                            disabled={loading}
                            className="btn btn-outline-danger rounded-circle d-flex align-items-center justify-content-center"
                            title="Sign Out"
                            style={{ width: 40, height: 40 }}
                        >
                            {loading ? (
                                <span className="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" />
                            ) : (
                                <LogOut size={18} />
                            )}
                        </button>
                    </div>
                )}
            </div>
        </motion.aside>
    );
};

export default Sidebar;
